import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import SortableTable from "../../Componentes/Utils/SortableTable";
import useApi from "../../hooks/useApi";

const PAGE_SIZE = 100;
const SCROLL_THRESHOLD = 40;

const PageContent = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: 12px;
    box-sizing: border-box;
    overflow: hidden;
`;

const HeaderBar = styled.div`
    width: 100%;
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    align-items: center;
    flex-shrink: 0;
`;

const Badge = styled.div`
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.12);
    color: rgba(255, 255, 255, 0.9);
    padding: 8px 10px;
    border-radius: 10px;
    font-size: 13px;
    white-space: nowrap;
`;

const FilterInput = styled.input`
    height: 40px;
    flex: 1;
    min-width: 240px;
    border-radius: 12px;
    border: 1px solid rgba(255, 255, 255, 0.18);
    background: rgba(255, 255, 255, 0.08);
    color: #fff;
    padding: 0 12px;
    box-sizing: border-box;
    outline: none;

    &::placeholder {
        color: rgba(255, 255, 255, 0.7);
    }

    &:focus {
        border-color: rgba(0, 123, 255, 0.9);
        box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
    }
`;

const TableShell = styled.div`
    width: 100%;
    flex: 1;
    min-height: 0;
    background-color: #ffffff;
    border-radius: 15px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
`;

const TableFlexFill = styled.div`
    flex: 1;
    min-height: 0;
    width: 100%;
    display: flex;
    flex-direction: column;

    & > div {
        flex: 1;
        min-height: 0;
        display: flex;
        flex-direction: column;
    }
`;

function buildSearchHaystack(row) {
    if (!row) return "";

    return [row.user_id, row.grupo_nome]
        .filter((v) => v != null && String(v).trim() !== "")
        .join(" ")
        .toUpperCase();
}

const GestaoUsuarios = () => {
    const api = useApi();
    const tableRef = useRef(null);

    const [loading, setLoading] = useState(true);
    const [usuarios, setUsuarios] = useState([]);
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
    const [q, setQ] = useState("");

    useEffect(() => {
        const fetchUsuarios = async () => {
            setLoading(true);

            try {
                const { data } = await api.get("/Painel/API/AccessManagement/GestaoUsuarios/List/index.php");
                setUsuarios(Array.isArray(data) ? data : []);
            } catch (error) {
                console.error(error);
                setUsuarios([]);
            } finally {
                setLoading(false);
            }
        };

        fetchUsuarios();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filtered = useMemo(() => {
        const query = q.trim().toUpperCase();
        if (!query) return usuarios;

        return usuarios.filter((row) => buildSearchHaystack(row).includes(query));
    }, [usuarios, q]);

    useEffect(() => {
        setVisibleCount(PAGE_SIZE);
        if (tableRef.current) {
            tableRef.current.scrollTop = 0;
        }
    }, [filtered]);

    const loadMoreRows = useCallback(() => {
        setVisibleCount((prev) => {
            const total = filtered.length;
            if (prev >= total) return prev;
            return Math.min(prev + PAGE_SIZE, total);
        });
    }, [filtered.length]);

    const columns = useMemo(
        () => [
            {
                id: "user_id",
                header: "USUÁRIO",
                accessor: "user_id",
                sortable: true,
                width: "220px",
            },
            {
                id: "grupo_nome",
                header: "GRUPO",
                accessor: "grupo_nome",
                sortable: true,
                render: (value) => (value != null && String(value).trim() !== "" ? String(value).trim() : "—"),
            },
        ],
        []
    );

    return (
        <PageContent>
            <HeaderBar>
                <Badge>Total: {loading ? "..." : filtered.length}</Badge>
                <FilterInput
                    placeholder="Pesquisar por usuário ou grupo..."
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                />
            </HeaderBar>

            <TableShell>
                <TableFlexFill>
                    <SortableTable
                        ref={tableRef}
                        columns={columns}
                        data={filtered}
                        getRowId={(row) => row.id}
                        isLoading={loading}
                        loadingText="Carregando usuários..."
                        emptyText="NENHUM USUÁRIO ENCONTRADO"
                        initialSort={{ key: "user_id", direction: "asc" }}
                        limit={visibleCount}
                        onScrollReachEnd={loadMoreRows}
                        scrollReachThreshold={SCROLL_THRESHOLD}
                    />
                </TableFlexFill>
            </TableShell>
        </PageContent>
    );
};

export default GestaoUsuarios;
